import { useState, useEffect } from 'react';

/**
 * Wallet Selection Modal Component
 * Allows users to choose their preferred wallet provider
 */
export function WalletSelectionModal({ isOpen, onClose, onSelectWallet }) {
  const [availableWallets, setAvailableWallets] = useState([]);

  // Detect available wallets on mount
  useEffect(() => {
    if (isOpen) {
      detectWallets();
    }
  }, [isOpen]);

  const detectWallets = () => {
    const wallets = [];

    // Check for MetaMask
    if (typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask) {
      wallets.push({
        id: 'metamask',
        name: 'MetaMask',
        icon: '🦊',
        installed: true,
        description: 'Connect with MetaMask browser extension'
      });
    }

    // Check for Coinbase Wallet
    if (typeof window.ethereum !== 'undefined' && window.ethereum.isCoinbaseWallet) {
      wallets.push({
        id: 'coinbase',
        name: 'Coinbase Wallet',
        icon: '🔵',
        installed: true,
        description: 'Connect with Coinbase Wallet'
      });
    }

    // Check for Trust Wallet
    if (typeof window.ethereum !== 'undefined' && window.ethereum.isTrust) {
      wallets.push({
        id: 'trust',
        name: 'Trust Wallet',
        icon: '⭐',
        installed: true,
        description: 'Connect with Trust Wallet'
      });
    }

    // Check for generic injected wallet (if none of the above)
    if (typeof window.ethereum !== 'undefined' &&
        !window.ethereum.isMetaMask &&
        !window.ethereum.isCoinbaseWallet &&
        !window.ethereum.isTrust) {
      wallets.push({
        id: 'injected',
        name: 'Injected Wallet',
        icon: '👛',
        installed: true,
        description: 'Connect with your browser wallet'
      });
    }

    // WalletConnect is always available
    wallets.push({
      id: 'walletconnect',
      name: 'WalletConnect',
      icon: '🔗',
      installed: true,
      description: 'Scan QR code with your mobile wallet'
    });

    // Show popular wallets that aren't installed
    if (!wallets.some(w => w.id === 'metamask')) {
      wallets.push({
        id: 'metamask',
        name: 'MetaMask',
        icon: '🦊',
        installed: false,
        description: 'Install MetaMask extension',
        installUrl: 'https://metamask.io/download/'
      });
    }

    setAvailableWallets(wallets);
  };

  const handleWalletClick = (wallet) => {
    if (wallet.installed) {
      onSelectWallet(wallet.id);
      onClose();
    } else if (wallet.installUrl) {
      window.open(wallet.installUrl, '_blank');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="t24-wallet-modal-overlay" onClick={handleBackdropClick}>
      <div className="t24-wallet-modal t24-glass-card">
        <div className="t24-wallet-modal-header">
          <h2 className="t24-wallet-modal-title">Connect Wallet</h2>
          <button
            className="t24-wallet-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="t24-wallet-modal-body">
          <p className="t24-wallet-modal-description">
            Choose your preferred wallet to connect to Trabajo24 Validator Network
          </p>

          <div className="t24-wallet-options">
            {availableWallets.map((wallet) => (
              <button
                key={wallet.id}
                className={`t24-wallet-option ${!wallet.installed ? 't24-wallet-option-disabled' : ''}`}
                onClick={() => handleWalletClick(wallet)}
              >
                <div className="t24-wallet-option-icon">{wallet.icon}</div>
                <div className="t24-wallet-option-info">
                  <div className="t24-wallet-option-name">{wallet.name}</div>
                  <div className="t24-wallet-option-description">{wallet.description}</div>
                </div>
                <div className="t24-wallet-option-status">
                  {wallet.installed ? (
                    <span className="t24-wallet-status-badge t24-wallet-status-installed">✓</span>
                  ) : (
                    <span className="t24-wallet-status-badge t24-wallet-status-notinstalled">Install</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="t24-wallet-modal-footer">
            <p className="t24-wallet-modal-help">
              New to Ethereum wallets? <a href="https://ethereum.org/en/wallets/" target="_blank" rel="noopener noreferrer">Learn more</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
